import React, { useState } from 'react';
import { CheckCircle, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiSendInvite, formatApiError } from '../../lib/api';
import { OFFICES } from '../../lib/config';

/**
 * UserManagementPanel — admin-only panel for sending user invitations.
 * Renders null for non-admin users.
 * Matches the panel/form styling of NotificationsPanel and Login.
 */
export default function UserManagementPanel() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [officeId, setOfficeId] = useState('developments');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  if (user?.role !== 'admin') {
    return null;
  }

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await apiSendInvite({ email, office_id: officeId });
      setSuccess(`Invite sent to ${email}`);
      setEmail('');
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="user-management-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-2">
          <UserPlus size={15} className="text-gray-500" />
          <span className="text-xs font-semibold text-gray-700">Send Invitation</span>
        </div>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: '#DBEAFE', color: '#3498DB' }}
        >
          Admin
        </span>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSendInvite} className="space-y-4 max-w-sm">
          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-shadow"
              data-testid="invite-email-input"
            />
          </div>

          {/* Office */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
              Office
            </label>
            <select
              value={officeId}
              onChange={(e) => setOfficeId(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-shadow"
              data-testid="invite-office-select"
            >
              {OFFICES.map((office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500 font-medium" data-testid="invite-error">
              {error}
            </p>
          )}

          {/* Success */}
          {success && (
            <div
              className="flex items-center gap-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
              data-testid="invite-success"
            >
              <CheckCircle size={13} />
              {success}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg py-2.5 text-sm font-semibold text-white transition-all active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: '#111827' }}
            data-testid="invite-submit-button"
          >
            {loading ? 'Sending...' : 'Send Invite'}
          </button>
        </form>
      </div>
    </div>
  );
}
